import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Button } from 'govuk-react-jsx';
import { useSelector } from 'react-redux';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
  getDashboardLink,
} from '../../features/app/appSlice';
import { getGrantTypeDynamicWording } from '../../features/installer/installerJourneySlice';
import { getSubmittedApplicationId } from '../../features/dashboard/dashboardSlice';

import { getApplicationClaim, getWordingTemplates } from '../../firebase';
import useTitle from '../../hooks/useTitle';
import useGaEndEvent from '../../hooks/useGaEndEvent';
import { useSetReduxState } from '../../hooks/useResetReduxState';

const ApplicationSubmitted = () => {
  const grantTypeDynamicWording = useSelector(getGrantTypeDynamicWording);
  const [isLoading, setIsLoading] = useState(true);

  const [setResetReduxState] = useSetReduxState();
  const dashboardLink = useSelector(getDashboardLink);
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  const [dynamicWording, setDynamicWording] = useState({
    message: null,
    is_dynamic: false,
  });
  useTitle(
    'pmcApp.ApplicationSubmitted.s27',
    'You have submitted your application - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  useGaEndEvent('PmcSubmitClaim', 'pmc_claim_end');
  const [claimData, setClaimData] = useState({});

  const applicationId = useSelector(getSubmittedApplicationId);

  const handleButton = () => {
    setResetReduxState('pmcJourney', 'applicationJourney');
    window.location.assign(dashboardLink);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // ✅ FIXED: Moved inside try block and made async
        if (dashboardLink) {
          const claimResponse = await getApplicationClaim({
            id: applicationId,
          });
          const { data } = claimResponse;

          const claimDeadlineDate = new Date(
            // eslint-disable-next-line no-underscore-dangle
            data.claim_submission_deadline_date._seconds * 1000
          );
          const claimDeadlineDateFormatted = format(
            claimDeadlineDate,
            'dd MMMM yyyy'
          );
          const newClaimData = {
            ...data,
            claimDeadlineDateFormatted,
          };
          await setClaimData(newClaimData);
          setIsLoading(true);
          const wordingResponse = await getWordingTemplates({
            grantType: newClaimData.grant_type,
          });

          if (wordingResponse.data?.success && wordingResponse.data?.data) {
            await setDynamicWording({
              message: wordingResponse.data.data,
              is_dynamic: wordingResponse.data.data.is_dynamic,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.code === 'functions/permission-denied') {
          window.location.assign(dashboardLink);
        }
      } finally {
        setIsLoading(false); // ✅ End loading
      }
    };

    fetchData(); // ✅ FIXED: Call the async function
  }, [applicationId, dashboardLink, grantTypeDynamicWording, currentLanguage]); // ✅ FIXED: Added missing dependencies
  const getMessage = () => {
    // Before activation → Show hardcoded

    if (!dynamicWording.is_dynamic) {
      return copyProvider.getCopy(
        'pmcApp.ApplicationSubmitted.s21',
        'You will have 7 working days to challenge it. If no action is taken, we will accept the claim and approve for payment.'
      );
    }

    // After activation:

    const message =
      currentLanguage === 'en'
        ? dynamicWording.message.workingDaysChallenge7_PMCmessage_en
        : dynamicWording.message.workingDaysChallenge7_PMCmessage_cy;

    if (message === '') {
      return '';
    }

    // If null or undefined → Show hardcoded
    if (message === null || message === undefined) {
      return copyProvider.getCopy(
        'pmcApp.ApplicationSubmitted.s21',
        'You will have 7 working days to challenge it. If no action is taken, we will accept the claim and approve for payment.'
      );
    }

    // If has value → Show dynamic text
    return message;
  };
  if (isLoading) {
    return (
      <div className="govuk-grid-row">
        <p className="govuk-body">Loading...</p>
      </div>
    );
  }
  const pageModel = (
    <div>
      <div className="govuk-panel govuk-panel--confirmation">
        <h1 className="govuk-panel__title">
          {copyProvider.getCopy(
            'pmcApp.ApplicationSubmitted.s1',
            'Application submitted'
          )}
        </h1>
        <div className="govuk-panel__body">
          {copyProvider.getCopy(
            'pmcApp.ApplicationSubmitted.s2',
            'Your reference number'
          )}
          <br />
          <strong>{claimData.claim_id}</strong>
        </div>
      </div>
      <p className="govuk-body">
        {copyProvider.getCopy(
          'pmcApp.ApplicationSubmitted.s3',
          'We have emailed your installer to let them know you have finished your part of the application.'
        )}
      </p>

      <div>
        <h2 className="govuk-heading-m">
          {copyProvider.getCopy(
            'pmcApp.ApplicationSubmitted.s5',
            'What happens now'
          )}
        </h2>
        <p className="govuk-body">
          {copyProvider.getCopy(
            'pmcApp.ApplicationSubmitted.s7',
            'You can now contact your installer to get the chargepoints installed.'
          )}
        </p>
        <h2 className="govuk-heading-m">
          {copyProvider.getCopy(
            'pmcApp.ApplicationSubmitted.s9',
            'What happens after the installation'
          )}
        </h2>
        <h3 className="govuk-heading-s">
          {copyProvider.getCopy(
            'pmcApp.ApplicationSubmitted.s13',
            'Installer will submit their part of the claim'
          )}
        </h3>
        <p className="govuk-body">
          {copyProvider.getCopy(
            'pmcApp.ApplicationSubmitted.s14',
            'When the installation is finished, your installer will have to submit the evidence. This includes photos, chargepoint serial numbers and the invoice they have given to you.'
          )}
        </p>
        <h3 className="govuk-heading-s">
          {copyProvider.getCopy(
            'pmcApp.ApplicationSubmitted.s19',
            'You can review the claim submitted by your installer'
          )}
        </h3>
        <p className="govuk-body">
          {copyProvider.getCopy(
            'pmcApp.ApplicationSubmitted.s20',
            'When your installer submits the claim for this application, you will receive an email to review it.'
          )}
        </p>
        <p className="govuk-body">{getMessage()}</p>
      </div>

      <Button
        className="govuk-button govuk-!-margin-top-4"
        onClick={handleButton}
      >
        {copyProvider.getCopy(
          'pmcApp.ApplicationSubmitted.s22',
          'Manage your applications'
        )}
      </Button>

      <h3 className="govuk-heading-s govuk-!-margin-top-4">
        {copyProvider.getCopy(
          'pmcApp.ApplicationSubmitted.s23',
          'Help to make this service better'
        )}
      </h3>
      <p className="govuk-body">
        <Link
          to={{}}
          onClick={() =>
            window.open('https://www.smartsurvey.co.uk/s/EV-grants/', '_blank')
          }
          className="govuk-link"
          target="_blank"
          rel="noreferrer"
        >
          {copyProvider.getCopy(
            'pmcApp.ApplicationSubmitted.s24',
            'What did you think of this service? '
          )}
          <span className="govuk-visually-hidden">
            {copyProvider.getCopy(
              'pmcApp.ApplicationSubmitted.s25',
              '(opens in a new tab)'
            )}
          </span>
        </Link>
        {copyProvider.getCopy(
          'pmcApp.ApplicationSubmitted.s26',
          '(takes 30 seconds)'
        )}
      </p>
    </div>
  );
  return pageModel;
};

export default ApplicationSubmitted;
